import { Button } from "antd";
import classNames from "classnames";
import { AngleDownIcon } from "components/Icons";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getLocalStorage, setLocalStorage } from "utils/localStorageUtils";

import CheckIcon from "ac-components/ac-icons/CheckIcon";
import PlusIconCircleBorder from "ac-components/ac-icons/PlusIconCircleBorder";

import "./ACColorPicker.less";
import {
  BROWSER_PALETTE_DELAY,
  MAX_RECENT_COLORS,
  PREDEFINED_COLOR_COLUMNS,
  RECENT_COLORS_STORAGE_KEY,
} from "./constants";

type ACColorPickerProps = {
  value?: string;
  onChange?: (color: string) => void;
};

function getRecentColors(): string[] {
  return getLocalStorage<string[]>(RECENT_COLORS_STORAGE_KEY, []);
}

function saveRecentColors(colors: string[]): void {
  setLocalStorage(RECENT_COLORS_STORAGE_KEY, colors);
}

function addToRecent(color: string, current: string[]): string[] {
  const normalized = _.toLower(color);
  const filtered = _.reject(current, (c) => _.toLower(c) === normalized);
  return _.take([normalized, ...filtered], MAX_RECENT_COLORS);
}

export default function ACColorPicker({
  value = "#000000",
  onChange,
}: ACColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [recentColors, setRecentColors] = useState<string[]>(getRecentColors);
  const containerRef = useRef<HTMLDivElement>(null);
  const nativePickerRef = useRef<HTMLInputElement>(null);

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev && value) {
        setRecentColors((current) => {
          const updated = addToRecent(value, current);
          saveRecentColors(updated);
          return updated;
        });
      }
      return !prev;
    });
  }, [value]);

  const handleSelectColor = useCallback(
    (color: string) => {
      debouncedCommitRef.current?.cancel();
      setRecentColors((current) => {
        const updated = addToRecent(color, current);
        saveRecentColors(updated);
        return updated;
      });
      onChange?.(color);
      setOpen(false);
    },
    [onChange],
  );

  // Debounced commit for the native browser color picker.
  // React's onChange (= DOM "input") fires on every drag movement.
  // We debounce so only the latest value is committed.
  const debouncedCommitRef = useRef(
    _.debounce((color: string, onChangeFn?: (c: string) => void) => {
      setRecentColors((current) => {
        const updated = addToRecent(color, current);
        saveRecentColors(updated);
        return updated;
      });
      onChangeFn?.(color);
    }, BROWSER_PALETTE_DELAY),
  );

  useEffect(() => {
    return () => debouncedCommitRef.current?.cancel();
  }, []);

  const handleNativePickerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedCommitRef.current(e.target.value, onChange);
    },
    [onChange],
  );

  useEffect(() => {
    if (!open) return;
    if (nativePickerRef.current) {
      nativePickerRef.current.value = value;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, value]);

  return (
    <div className="ac-color-picker" ref={containerRef}>
      <Button
        onClick={handleToggle}
        className={classNames(
          "ac-color-picker__trigger filter-bar-dropdown-button",
        )}
      >
        <span
          className="ac-color-picker__swatch"
          style={{ backgroundColor: value }}
        />
        <AngleDownIcon className="ac-color-picker__chevron m-l-0 font-xs" />
      </Button>

      {open && (
        <div className="ac-color-picker__popover">
          <div className="ac-color-picker__palette">
            {_.map(PREDEFINED_COLOR_COLUMNS, (column, colIdx) =>
              _.map(column, (color, rowIdx) => (
                <button
                  key={`${colIdx}-${rowIdx}`}
                  type="button"
                  className={`ac-color-picker__color-btn${_.toLower(value) === _.toLower(color) ? " ac-color-picker__color-btn--selected" : ""}`}
                  style={{
                    backgroundColor: color,
                    gridColumn: colIdx + 1,
                    gridRow: rowIdx + 1,
                  }}
                  onClick={() => handleSelectColor(color)}
                  title={color}
                >
                  {_.toLower(value) === _.toLower(color) && (
                    <CheckIcon className="ac-color-picker__check" />
                  )}
                </button>
              )),
            )}
          </div>

          <div className="ac-color-picker__divider" />

          <div className="ac-color-picker__recent">
            <div className="ac-color-picker__recent-header">
              <span className="ac-color-picker__recent-label">Recent</span>
              <div className="ac-color-picker__add-btn-wrap">
                <button
                  type="button"
                  className="ac-color-picker__add-btn"
                  onClick={() => nativePickerRef.current?.click()}
                  title="Add custom color"
                >
                  <PlusIconCircleBorder className="ac-color-picker__add-icon" />
                </button>
                <input
                  ref={nativePickerRef}
                  type="color"
                  onChange={handleNativePickerChange}
                  className="ac-color-picker__native-input"
                />
              </div>
            </div>

            <div className="ac-color-picker__recent-grid">
              {_.map(recentColors, (color) => (
                <button
                  key={color}
                  type="button"
                  className={`ac-color-picker__color-btn${_.toLower(value) === _.toLower(color) ? " ac-color-picker__color-btn--selected" : ""}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleSelectColor(color)}
                  title={color}
                >
                  {_.toLower(value) === _.toLower(color) && (
                    <CheckIcon className="ac-color-picker__check" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
